import { FOLLOW_URL, PRIVACY_URL, WAITLIST_ENABLED } from "../config.js";
import { APP_TEASER } from "../site-copy.js";
import { h } from "../ui/dom.js";

/**
 * The CTA strip.
 *
 * Email capture is gated on the published privacy policy and explicit consent
 * (#2064; the policy is PR #1807, unmerged). `WAITLIST_ENABLED` is therefore
 * OFF by default and additionally refuses to turn on without a
 * `VITE_PRIVACY_URL` — so the shipped default is the announcement below, with
 * no field to type an address into and no request to /api/waitlist.
 */
export function waitlist(): HTMLElement {
  return WAITLIST_ENABLED ? signupStrip() : announcementStrip();
}

/**
 * The default: say what is coming, collect nothing.
 *
 * A call to action needs an action. With no signup and no follow link there is
 * nothing to press, so the announcement is one quiet line rather than a boxed
 * CTA — and it no longer apologises for the privacy policy, which the footer
 * already links.
 */
function announcementStrip(): HTMLElement {
  const line = `Arrow Chess is coming to iPhone: ${APP_TEASER.charAt(0).toLowerCase()}${APP_TEASER.slice(1)}`;
  if (!FOLLOW_URL) return h("section", { class: "section" }, h("p", { class: "note", text: line }));
  return h(
    "section",
    { class: "waitlist" },
    h("h2", { text: "Coming to iPhone" }),
    h("p", { class: "note", text: APP_TEASER }),
    h("p", { class: "form-msg" }, h("a", { href: FOLLOW_URL, target: "_blank", rel: "noopener noreferrer", text: "Follow along for the launch date" })),
  );
}

/**
 * The signup, only reachable with the gate on.
 *
 * The address goes to a Cloudflare Pages Function that holds the Buttondown
 * key — the browser never sees a vendor credential. Consent is a separate,
 * unticked box, not a pre-checked one and not implied by pressing the button;
 * double opt-in at the vendor is why the success copy says to check your email.
 */
function signupStrip(): HTMLElement {
  const email = h("input", {
    type: "email",
    name: "email",
    required: true,
    autocomplete: "email",
    placeholder: "you@example.com",
    "aria-label": "Email address",
  });
  const consent = h("input", { type: "checkbox", id: "waitlist-consent", name: "consent" });
  // A honeypot, not a CAPTCHA: a real field a person never sees or tabs into.
  const trap = h("input", { type: "text", name: "company", tabindex: "-1", autocomplete: "off", "aria-hidden": "true" });
  const submit = h("button", { class: "btn", type: "submit", text: "Join the waitlist" });
  const msg = h("p", { class: "form-msg", role: "status", "aria-live": "polite" });

  const consentRow = h(
    "label",
    { class: "consent", for: "waitlist-consent" },
    consent,
    h(
      "span",
      {},
      "I agree to receive launch news by email. You’ll get a confirmation email first, and you can unsubscribe any time. See our ",
      h("a", { href: PRIVACY_URL, target: "_blank", rel: "noopener noreferrer", text: "privacy policy" }),
      ".",
    ),
  );

  const form = h(
    "form",
    {
      novalidate: true,
      onsubmit: (event: Event) => {
        event.preventDefault();
        void send();
      },
    },
    email,
    h("span", { class: "hp" }, h("label", { text: "Company" }), trap),
    submit,
  );

  async function send(): Promise<void> {
    const address = email.value.trim();
    if (!address.includes("@") || address.length < 5) {
      msg.textContent = "That email address doesn’t look quite right. Check it and try again.";
      return;
    }
    if (!consent.checked) {
      msg.textContent = "Tick the box first — we only email people who’ve asked us to.";
      return;
    }
    submit.disabled = true;
    msg.textContent = "Sending…";
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: address, company: trap.value, consent: true }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) {
        msg.textContent = "Check your inbox — click the link to confirm, and you’re on the list.";
        form.replaceChildren();
        consentRow.remove();
        return;
      }
      msg.textContent =
        res.status === 429
          ? "Too many tries. Give it a minute."
          : (body.error ?? "Something went wrong. Try again?");
    } catch {
      msg.textContent = "We couldn’t connect. Check your connection and try again.";
    } finally {
      submit.disabled = false;
    }
  }

  return h(
    "section",
    { class: "waitlist" },
    h("h2", { text: "Coming to iPhone" }),
    h("p", {
      class: "note",
      text: `${APP_TEASER} Leave your email and we’ll write once, when it’s out.`,
    }),
    form,
    consentRow,
    msg,
  );
}
